import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Link } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import {
  Database, RefreshCw, Users, Wallet, Layers3, ArrowLeft, Save, Search, CheckCircle2, AlertTriangle, Plug, Unplug, ChevronRight, ChevronDown, Plus, Pencil, Trash2, X,
} from "lucide-react";

// Initial fallback — overridden by dynamic list loaded from /api/travel-expense-types
const DEFAULT_CATEGORIES = ["meals", "lodging", "transport", "mileage", "per_diem", "other"];

/* Build a parent → children tree from a flat list of QBO Accounts, then flatten
 * it back to a pre-ordered list of `{ node, depth }` for rendering. Avoids a
 * recursive React component (which can blow up Babel's AST traversal). */
function buildAccountTree(accounts) {
  const byId = new Map();
  accounts.forEach((a) => byId.set(String(a.Id), { ...a, _children: [] }));
  const roots = [];
  byId.forEach((node) => {
    const parentId = (node.ParentRef || {}).value ? String(node.ParentRef.value) : null;
    if (parentId && byId.has(parentId)) {
      byId.get(parentId)._children.push(node);
    } else {
      roots.push(node);
    }
  });
  const sortRec = (arr) => {
    arr.sort((a, b) => String(a.Name || "").localeCompare(String(b.Name || "")));
    arr.forEach((n) => sortRec(n._children));
  };
  sortRec(roots);
  return roots;
}

function flattenTree(roots) {
  const out = [];
  const walk = (arr, depth) => {
    arr.forEach((n) => {
      out.push({ node: n, depth });
      if (n._children && n._children.length) walk(n._children, depth + 1);
    });
  };
  walk(roots, 0);
  return out;
}

function subtreeMatches(node, q) {
  if (!q) return true;
  const hay = `${node.Name || ""} ${node.FullyQualifiedName || ""} ${node.AccountType || ""}`.toLowerCase();
  if (hay.includes(q)) return true;
  return (node._children || []).some((c) => subtreeMatches(c, q));
}

function CategoryTree({ accounts, selected, setSelected, onSaveSelection, savingSelection, testid }) {
  const { t } = useI18n();
  const tree = useMemo(() => buildAccountTree(accounts || []), [accounts]);
  const [expanded, setExpanded] = useState(() => new Set());
  const [query, setQuery] = useState("");
  const q = (query || "").toLowerCase();
  const flat = useMemo(() => flattenTree(tree), [tree]);

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const expandAll = () => {
    const all = new Set();
    flat.forEach(({ node }) => { if ((node._children || []).length) all.add(String(node.Id)); });
    setExpanded(all);
  };
  const collapseAll = () => setExpanded(new Set());
  const onToggleSelected = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  /* Visible rows = pre-order traversal of `tree`, filtering by:
   *   - search query (keep node if it or any descendant matches)
   *   - collapsed state (hide descendants of collapsed nodes) */
  const visibleRows = useMemo(() => {
    const rows = [];
    const walk = (nodes, depth, parentCollapsed) => {
      nodes.forEach((n) => {
        const id = String(n.Id);
        const isOpen = expanded.has(id);
        if (parentCollapsed) {
          // skip entire subtree if any ancestor is collapsed
          return;
        }
        const matchesQ = !q || subtreeMatches(n, q);
        if (!matchesQ) return;
        rows.push({ node: n, depth });
        if ((n._children || []).length && (isOpen || q)) {
          // when searching, auto-expand to show matching descendants
          walk(n._children, depth + 1, false);
        }
      });
    };
    walk(tree, 0, false);
    return rows;
  }, [tree, expanded, q]);

  return (
    <div className="sw-card !p-0 overflow-hidden" data-testid={testid}>
      <div className="p-3 border-b border-[#e2e8f0] flex items-center gap-2 flex-wrap">
        <Search size={14} className="text-[#94a3b8]" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("qbo.search_catalog_ph")}
          className="sw-input !py-1 !text-[12px] flex-1 min-w-[120px] !border-0 !shadow-none"
        />
        <button onClick={expandAll} className="sw-btn-secondary !text-[11px] !py-1" data-testid="qbo-cat-expand-all">{t("qbo.expand_all")}</button>
        <button onClick={collapseAll} className="sw-btn-secondary !text-[11px] !py-1" data-testid="qbo-cat-collapse-all">{t("qbo.collapse_all")}</button>
        <span className="text-[11px] text-[#94a3b8]">{accounts.length} contas · <strong className="text-[#475569]">{selected.length}</strong> sel.</span>
        <button onClick={onSaveSelection} disabled={savingSelection} className="sw-btn-secondary !text-[11px] !py-1" data-testid={`${testid}-save-selection`}>
          <Save size={11} /> {savingSelection ? "…" : t("qbo.save_selection")}
        </button>
      </div>
      <div className="max-h-[420px] overflow-y-auto py-1 px-1">
        {visibleRows.length === 0 && (
          <div className="text-center text-[12px] text-[#94a3b8] py-6 italic">
            {accounts.length === 0
              ? t("qbo.no_accounts_synced")
              : t("qbo.no_match_search")}
          </div>
        )}
        {visibleRows.map(({ node, depth }) => {
          const id = String(node.Id);
          const hasChildren = (node._children || []).length > 0;
          const isOpen = expanded.has(id);
          return (
            <div
              key={id}
              className={`flex items-center gap-2 py-1.5 px-2 rounded-[8px] hover:bg-[#f8fafc] ${selected.includes(id) ? "bg-[#f0f9ff]" : ""}`}
              style={{ paddingLeft: 8 + depth * 18 }}
              data-testid={`qbo-cat-node-${id}`}
            >
              {hasChildren ? (
                <button onClick={() => toggle(id)} className="text-[#94a3b8] hover:text-[#1e293b] shrink-0" data-testid={`qbo-cat-toggle-${id}`}>
                  {isOpen || q ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
              ) : (
                <span className="w-[14px] inline-block shrink-0" />
              )}
              <input
                type="checkbox"
                checked={selected.includes(id)}
                onChange={() => onToggleSelected(id)}
                data-testid={`qbo-cat-check-${id}`}
              />
              <span
                className={`text-[12.5px] flex-1 min-w-0 truncate ${node.Active === false ? "text-[#94a3b8] italic" : "text-[#1e293b]"}`}
                title={node.FullyQualifiedName}
              >
                {node.Name}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-[#94a3b8] shrink-0">{node.AccountType || ""}</span>
              {node.AccountSubType && (
                <span className="text-[10px] text-[#cbd5e1] shrink-0 hidden sm:inline">{node.AccountSubType}</span>
              )}
              <span className="text-[10px] text-[#cbd5e1] shrink-0">#{id}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function SyncCard({ icon: Icon, label, count, lastSyncedAt, onSync, syncing, testid }) {
  const { t } = useI18n();
  return (
    <div className="sw-card !p-5" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-[12px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center"><Icon size={18} /></div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">{label}</div>
            <div className="font-semibold text-[22px] text-[#1e293b]">{count.toLocaleString("en-US")}</div>
          </div>
        </div>
        <button onClick={onSync} disabled={syncing} className="sw-btn-secondary !text-[12px]">
          <RefreshCw size={12} className={syncing ? "animate-spin" : ""} />
          {syncing ? t("qbo.syncing") : t("qbo.sync")}
        </button>
      </div>
      <div className="text-[11px] text-[#94a3b8]">
        {lastSyncedAt ? `${t("qbo.last_sync")} ${lastSyncedAt.slice(0,16).replace("T"," ")} UTC` : t("qbo.never_synced")}
      </div>
    </div>
  );
}

function CatalogList({ items, columns, search, setSearch, selected, setSelected, onSaveSelection, savingSelection, testid, extraColumns }) {
  const { t } = useI18n();
  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter((r) => columns.some((c) => String(r[c.key] || "").toLowerCase().includes(q)));
  }, [items, search, columns]);
  const filteredIds = filtered.map((r) => String(r.Id));
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.includes(id));
  const someFilteredSelected = filteredIds.some((id) => selected.includes(id));
  const toggleAllFiltered = () => {
    if (allFilteredSelected) {
      setSelected(selected.filter((id) => !filteredIds.includes(id)));
    } else {
      const set = new Set([...selected, ...filteredIds]);
      setSelected(Array.from(set));
    }
  };
  const toggleOne = (id) => {
    if (selected.includes(id)) setSelected(selected.filter((x) => x !== id));
    else setSelected([...selected, id]);
  };
  const totalColumns = columns.length + (extraColumns?.length || 0);
  return (
    <div className="sw-card !p-0 overflow-hidden" data-testid={testid}>
      <div className="p-3 border-b border-[#e2e8f0] flex items-center gap-2 flex-wrap">
        <Search size={14} className="text-[#94a3b8]" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("qbo.search_ph")}
          className="sw-input !py-1 !text-[12px] flex-1 min-w-[120px] !border-0 !shadow-none"
        />
        <span className="text-[11px] text-[#94a3b8]">{filtered.length} / {items.length} · <strong className="text-[#475569]">{selected.length}</strong> sel.</span>
        <button
          data-testid={`${testid}-save-selection`}
          onClick={onSaveSelection}
          disabled={savingSelection}
          className="sw-btn-secondary !text-[11px] !py-1"
        >
          <Save size={11} /> {savingSelection ? "…" : t("qbo.save_selection")}
        </button>
      </div>
      <div className="max-h-[360px] overflow-y-auto">
        <table className="w-full text-[12.5px]">
          <thead className="bg-[#f8fafc] sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 w-8">
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  ref={(el) => el && (el.indeterminate = !allFilteredSelected && someFilteredSelected)}
                  onChange={toggleAllFiltered}
                  data-testid={`${testid}-check-all`}
                />
              </th>
              {columns.map((c) => <th key={c.key} className="text-left px-3 py-2 text-[10.5px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">{c.label}</th>)}
              {(extraColumns || []).map((c) => <th key={c.key} className="text-left px-3 py-2 text-[10.5px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 200).map((r, i) => {
              const id = String(r.Id);
              const checked = selected.includes(id);
              return (
                <tr key={id || i} className={`border-t border-[#f1f5f9] hover:bg-[#f8fafc] ${checked ? "bg-[#f0f9ff]" : ""}`}>
                  <td className="px-3 py-1.5">
                    <input type="checkbox" checked={checked} onChange={() => toggleOne(id)} data-testid={`${testid}-check-${id}`} />
                  </td>
                  {columns.map((c) => <td key={c.key} className="px-3 py-1.5 text-[#475569]">{r[c.key] ?? "—"}</td>)}
                  {(extraColumns || []).map((c) => <td key={c.key} className="px-3 py-1.5">{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>)}
                </tr>
              );
            })}
            {filtered.length > 200 && (
              <tr><td colSpan={totalColumns + 1} className="px-3 py-2 text-center text-[11px] text-[#94a3b8]">Mostrando primeiros 200 — refine a busca para ver os demais</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Section({ id, title, subtitle, defaultOpen = false, right, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="sw-card !p-0 overflow-hidden" data-testid={`qbo-section-${id}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid={`qbo-section-toggle-${id}`}
        className="w-full flex items-center justify-between px-5 py-4 bg-[#f8fafc] hover:bg-[#f1f5f9] transition-colors border-b border-[#eeeded]"
      >
        <div className="text-left flex-1">
          <div className="font-medium text-[15px] text-[#0f172a]">{title}</div>
          {subtitle && <div className="text-[12px] text-[#94a3b8] mt-0.5">{subtitle}</div>}
        </div>
        <div className="flex items-center gap-3">
          {right && <div onClick={(e) => e.stopPropagation()}>{right}</div>}
          {open ? <ChevronDown size={18} className="text-[#475569]" /> : <ChevronRight size={18} className="text-[#475569]" />}
        </div>
      </button>
      {open && <div className="p-5">{children}</div>}
    </div>
  );
}

export default function QBOMapping({ embedded = false }) {
  const { t } = useI18n();
  const [vendors, setVendors] = useState({ items: [], count: 0, last_synced_at: null, selected_ids: [] });
  const [categories, setCategories] = useState({ items: [], count: 0, last_synced_at: null, selected_ids: [] });
  const [classes, setClasses] = useState({ items: [], count: 0, last_synced_at: null, selected_ids: [] });
  const [selVendors, setSelVendors] = useState([]);
  const [selCategories, setSelCategories] = useState([]);
  const [selClasses, setSelClasses] = useState([]);
  const [savingSel, setSavingSel] = useState({ vendors: false, categories: false, classes: false });
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [mapping, setMapping] = useState({ categories: {}, departments: {}, class_accounts: {} });
  const [syncing, setSyncing] = useState({ vendors: false, categories: false, classes: false, all: false, employees: false });
  const [searchV, setSearchV] = useState(""); const [searchA, setSearchA] = useState(""); const [searchC, setSearchC] = useState("");
  const [empSearch, setEmpSearch] = useState("");
  const [savingMapping, setSavingMapping] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [qboStatus, setQboStatus] = useState(null);
  const [qboSettings, setQboSettings] = useState(null);
  const [showConnect, setShowConnect] = useState(false);
  const [connectForm, setConnectForm] = useState({ token_broker_url: "", environment: "production", client_id: "", client_secret: "", realm_id: "", refresh_token: "" });
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const [bulkRetrying, setBulkRetrying] = useState(false);
  const [bulkRetryResult, setBulkRetryResult] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadQboMeta = async () => {
    try {
      const [s, sett] = await Promise.all([
        api.get("/qbo/status"),
        api.get("/qbo/settings"),
      ]);
      setQboStatus(s.data);
      setQboSettings(sett.data);
    } catch (e) { /* non-fatal */ }
  };

  const load = async () => {
    const [v, a, c, m, deps, us, et] = await Promise.all([
      api.get("/qbo/catalog/vendors"),
      api.get("/qbo/catalog/categories"),
      api.get("/qbo/catalog/classes"),
      api.get("/qbo/expense-mapping"),
      api.get("/departments"),
      api.get("/users"),
      api.get("/travel-expense-types").catch(() => ({ data: [] })),
    ]);
    setVendors(v.data || { items: [], count: 0, selected_ids: [] });
    setCategories(a.data || { items: [], count: 0, selected_ids: [] });
    setClasses(c.data || { items: [], count: 0, selected_ids: [] });
    setSelVendors((v.data?.selected_ids || []).map(String));
    setSelCategories((a.data?.selected_ids || []).map(String));
    setSelClasses((c.data?.selected_ids || []).map(String));
    setMapping({ categories: m.data?.categories || {}, departments: m.data?.departments || {}, class_accounts: m.data?.class_accounts || {} });
    setDepartments(deps.data || []);
    setUsers((us.data || []).filter((u) => u.active && u.role !== "admin"));
    setExpenseTypes(et.data || []);
  };

  useEffect(() => { load(); loadQboMeta(); }, []);

  const saveConnection = async () => {
    setSaving(true);
    setTestResult(null);
    try {
      // Strip empty fields (so partial updates don't blank existing ones)
      const payload = Object.fromEntries(Object.entries(connectForm).filter(([, v]) => v !== ""));
      const r = await api.put("/qbo/settings", payload);
      setShowConnect(false);
      setConnectForm({ token_broker_url: "", environment: "production", client_id: "", client_secret: "", realm_id: "", refresh_token: "" });
      await loadQboMeta();
      // In broker mode the PUT already validated end-to-end and returned the discovered realm.
      const savedRealm = r?.data?.realm_id;
      alert(savedRealm ? `${t("qbo.save_success")} (realm ${savedRealm})` : t("qbo.save_success"));
    } catch (e) {
      alert(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post("/qbo/test-connection");
      setTestResult({ ok: true, ...r.data });
    } catch (e) {
      setTestResult({ ok: false, message: e?.response?.data?.detail || t("common.error") });
    } finally { setTesting(false); }
  };

  const disconnectQbo = async () => {
    if (!window.confirm(t("qbo.disconnect_confirm"))) return;
    setSaving(true);
    try {
      await api.delete("/qbo/settings");
      await loadQboMeta();
      alert(t("qbo.disconnected"));
    } catch (e) {
      alert(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const sync = async (kind) => {
    setSyncing((s) => ({ ...s, [kind]: true }));
    try {
      await api.post(`/qbo/sync/${kind}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || `Erro sincronizando ${kind}`);
    } finally {
      setSyncing((s) => ({ ...s, [kind]: false }));
    }
  };

  const syncEmployee = async (userId) => {
    try {
      const r = await api.post(`/qbo/sync-team member/${userId}`);
      setUsers((us) => us.map((u) => (u.id === userId ? { ...u, qbo_vendor_id: r.data.qbo_vendor_id } : u)));
    } catch (e) {
      alert(e?.response?.data?.detail || "Erro");
    }
  };

  const syncAllEmployees = async () => {
    if (!window.confirm(t("qbo.create_vendors_confirm"))) return;
    setSyncing((s) => ({ ...s, employees: true }));
    try {
      const r = await api.post("/qbo/sync-team members");
      alert(t("qbo.sync_result", { ok: r.data.synced.length, fail: r.data.failed.length }));
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Erro");
    } finally {
      setSyncing((s) => ({ ...s, employees: false }));
    }
  };

  const saveMapping = async () => {
    setSavingMapping(true);
    try {
      const r = await api.put("/qbo/expense-mapping", mapping);
      setSavedAt(r.data.updated_at);
    } catch (e) {
      alert(e?.response?.data?.detail || "Erro");
    } finally {
      setSavingMapping(false);
    }
  };

  const saveSelection = async (kind, ids) => {
    setSavingSel((s) => ({ ...s, [kind]: true }));
    try {
      await api.put(`/qbo/catalog/${kind}/selection`, { ids });
    } catch (e) {
      alert(e?.response?.data?.detail || "Erro");
    } finally {
      setSavingSel((s) => ({ ...s, [kind]: false }));
    }
  };

  // Update the QBO class link on a Department (department becomes source of truth)
  const [savingDept, setSavingDept] = useState({});
  const updateDeptClass = async (deptId, qboClassId) => {
    setSavingDept((s) => ({ ...s, [deptId]: true }));
    try {
      await api.patch(`/departments/${deptId}`, { qbo_class_id: qboClassId || null });
      setDepartments((prev) => prev.map((d) => d.id === deptId ? { ...d, qbo_class_id: qboClassId || null } : d));
    } catch (e) {
      alert(e?.response?.data?.detail || "Erro");
    } finally {
      setSavingDept((s) => ({ ...s, [deptId]: false }));
    }
  };

  // Departments CRUD
  const emptyDept = { name: "", cost_code: "", parent_id: "" };
  const [deptEditing, setDeptEditing] = useState(null); // null | "new" | {id,...}
  const [deptForm, setDeptForm] = useState(emptyDept);
  const [savingDeptForm, setSavingDeptForm] = useState(false);
  const startNewDept = () => { setDeptForm(emptyDept); setDeptEditing("new"); };
  const startEditDept = (d) => { setDeptForm({ name: d.name || "", cost_code: d.cost_code || "", parent_id: d.parent_id || "" }); setDeptEditing(d); };
  const cancelDept = () => { setDeptEditing(null); setDeptForm(emptyDept); };
  const saveDept = async (e) => {
    e.preventDefault();
    setSavingDeptForm(true);
    try {
      const payload = { ...deptForm, parent_id: deptForm.parent_id || null };
      if (deptEditing === "new") await api.post("/departments", payload);
      else await api.patch(`/departments/${deptEditing.id}`, payload);
      const { data } = await api.get("/departments");
      setDepartments(data || []);
      cancelDept();
    } catch (err) {
      alert(err?.response?.data?.detail || "Erro");
    } finally {
      setSavingDeptForm(false);
    }
  };
  const removeDept = async (dept) => {
    if (!window.confirm(t("qbo.dept_delete_confirm", { name: dept.name }))) return;
    try {
      await api.delete(`/departments/${dept.id}`);
      setDepartments((prev) => prev.filter((d) => d.id !== dept.id));
    } catch (err) {
      alert(err?.response?.data?.detail || "Erro");
    }
  };

  // Class → Chart of Account mapping (per QBO Class)
  const [savingClassAccount, setSavingClassAccount] = useState({});
  const updateClassAccount = async (classId, accountId) => {
    setSavingClassAccount((s) => ({ ...s, [classId]: true }));
    try {
      const next = { ...(mapping.class_accounts || {}) };
      if (accountId) next[String(classId)] = String(accountId);
      else delete next[String(classId)];
      await api.put("/qbo/expense-mapping", { class_accounts: next });
      setMapping((m) => ({ ...m, class_accounts: next }));
    } catch (e) {
      alert(e?.response?.data?.detail || "Erro");
    } finally {
      setSavingClassAccount((s) => ({ ...s, [classId]: false }));
    }
  };

  // Narrow categories list by selected ids (if a selection was saved)
  const visibleCategories = useMemo(() => {
    const all = categories.items || [];
    if (selCategories.length === 0) return all;
    const sel = new Set(selCategories);
    return all.filter((a) => sel.has(String(a.Id)));
  }, [categories.items, selCategories]);

  /* Pre-order traversal of the account tree so the dropdown shows the
   * hierarchy with depth-based indentation, e.g.
   *   Operating expenses
   *     ↳ Travel
   *         ↳ Meals
   *         ↳ Lodging
   * Only includes accounts the admin "saved as selected" (or everything if
   * no selection persisted yet). */
  const visibleCategoryOptions = useMemo(() => {
    const tree = buildAccountTree(visibleCategories);
    const flat = [];
    const walk = (nodes, depth) => {
      nodes.forEach((n) => {
        flat.push({ ...n, _depth: depth });
        if (n._children && n._children.length) walk(n._children, depth + 1);
      });
    };
    walk(tree, 0);
    return flat;
  }, [visibleCategories]);

  const selectedClasses = useMemo(() => {
    const all = classes.items || [];
    if (selClasses.length === 0) return all;
    const sel = new Set(selClasses);
    return all.filter((c) => sel.has(String(c.Id)));
  }, [classes.items, selClasses]);

  const filteredEmployees = useMemo(() => {
    if (!empSearch) return users;
    const q = empSearch.toLowerCase();
    return users.filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(q));
  }, [users, empSearch]);

  return (
    <div className="flex flex-col gap-6">
      {!embedded && (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/app/admin" className="text-[#64748b] hover:text-[#1e293b]" data-testid="qbo-back-link"><ArrowLeft size={18} /></Link>
            <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center"><Database size={22} /></div>
            <div>
              <h1 className="sw-heading text-[26px]">{t("qbo.mapping_title")}</h1>
              <p className="text-[13px] text-[#747474]">{t("qbo.mapping_desc")}</p>
            </div>
          </div>
          <button data-testid="qbo-sync-all-btn" onClick={() => sync("all")} disabled={syncing.all} className="sw-btn-primary">
            <RefreshCw size={14} className={syncing.all ? "animate-spin" : ""} /> {t("qbo.sync_all")}
          </button>
        </div>
      )}
      {embedded && (
        <div className="flex justify-end">
          <button data-testid="qbo-sync-all-btn" onClick={() => sync("all")} disabled={syncing.all} className="sw-btn-primary">
            <RefreshCw size={14} className={syncing.all ? "animate-spin" : ""} /> {t("qbo.sync_all")}
          </button>
        </div>
      )}

      {/* Section 1: Connection */}
      <Section
        id="connection"
        title={t("qbo.section_connection")}
        subtitle={t("qbo.conn_desc")}
        defaultOpen={true}
        right={
          <div className="flex items-center gap-2">
            {qboStatus?.configured
              ? <span className="sw-badge sw-badge-approved text-[11px]" data-testid="qbo-conn-status">{t("qbo.conn_connected")}</span>
              : <span className="sw-badge sw-badge-neutral text-[11px]" data-testid="qbo-conn-status">{t("qbo.conn_disconnected")}</span>}
          </div>
        }
      >
        <div className="flex items-start justify-between flex-wrap gap-3" data-testid="qbo-connection-card">
          <div className="flex-1">
            {qboStatus?.configured && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-[11.5px] text-[#475569]">
                <div><span className="block text-[10px] uppercase tracking-wider text-[#94a3b8]">{t("qbo.tenant")}</span>{qboStatus.tenant_id}</div>
                <div><span className="block text-[10px] uppercase tracking-wider text-[#94a3b8]">{t("qbo.realm")}</span><code className="text-[11px]">{qboStatus.realm_id}</code></div>
                <div><span className="block text-[10px] uppercase tracking-wider text-[#94a3b8]">{t("qbo.environment")}</span>{qboStatus.environment}</div>
                <div><span className="block text-[10px] uppercase tracking-wider text-[#94a3b8]">{t("qbo.mode")}</span>
                  <span className="uppercase font-semibold">{qboStatus.mode || "oauth"}</span>
                </div>
                <div><span className="block text-[10px] uppercase tracking-wider text-[#94a3b8]">{t("qbo.dry_run")}</span>{qboStatus.dry_run ? t("qbo.yes") : t("qbo.no")}</div>
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {qboStatus?.configured && (
              <button data-testid="qbo-test-btn" onClick={testConnection} disabled={testing} className="sw-btn-secondary">
                {testing ? t("qbo.testing") : t("qbo.btn_test")}
              </button>
            )}
            {qboStatus?.configured && (
              <button
                data-testid="qbo-bulk-retry-btn"
                disabled={bulkRetrying}
                onClick={async () => {
                  if (!window.confirm(t("qbo.bulk_retry_confirm"))) return;
                  setBulkRetrying(true); setBulkRetryResult(null);
                  try {
                    const r = await api.post("/qbo/bulk-retry");
                    setBulkRetryResult(r.data);
                  } catch (e) {
                    setBulkRetryResult({ error: e?.response?.data?.detail || t("common.error") });
                  } finally { setBulkRetrying(false); }
                }}
                className="sw-btn-secondary"
              >
                <RefreshCw size={14} className={bulkRetrying ? "animate-spin" : ""} />
                {bulkRetrying ? t("qbo.bulk_retrying") : t("qbo.bulk_retry")}
              </button>
            )}
            <button data-testid="qbo-connect-btn" onClick={() => setShowConnect(true)} className="sw-btn-secondary">
              <Plug size={14} /> {qboStatus?.configured ? t("qbo.btn_update") : t("qbo.btn_connect")}
            </button>
            {qboStatus?.configured && (
              <button data-testid="qbo-disconnect-btn" onClick={disconnectQbo} disabled={saving} className="sw-btn-secondary !text-[#881a27]">
                <Unplug size={14} /> {t("qbo.btn_disconnect")}
              </button>
            )}
          </div>
        </div>

        {bulkRetryResult && (
          <div data-testid="qbo-bulk-retry-result" className={`mt-3 rounded-[10px] p-3 text-[12px] ${bulkRetryResult.error ? "bg-rose-50 border border-rose-200 text-rose-800" : "bg-emerald-50 border border-emerald-200 text-emerald-800"}`}>
            {bulkRetryResult.error
              ? `✕ ${bulkRetryResult.error}`
              : `✓ ${t("qbo.bulk_retry_done").replace("{synced}", bulkRetryResult.synced).replace("{errors}", bulkRetryResult.errors).replace("{skipped}", bulkRetryResult.skipped).replace("{total}", bulkRetryResult.total)}`}
          </div>
        )}

        {testResult && (
          <div data-testid="qbo-test-result" className={`mt-3 rounded-[10px] p-3 text-[12px] ${testResult.ok ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-rose-50 border border-rose-200 text-rose-800"}`}>
            {testResult.ok
              ? <>✓ {t("qbo.test_ok")} <strong>{testResult.company_name}</strong> · {testResult.legal_name} · {testResult.country} · realm <code>{testResult.realm_id}</code></>
              : <>✕ {testResult.message}</>}
          </div>
        )}

        {showConnect && (
          <div className="mt-5 pt-5 border-t border-[#eeeded] space-y-3" data-testid="qbo-connect-form">
            <div className="rounded-[12px] border border-[#e2e8f0] bg-[#f8fafc] p-3">
              <label className="sw-label">{t("qbo.broker_url_label")} <span className="text-rose-500">*</span></label>
              <input data-testid="qbo-broker-url" type="password" value={connectForm.token_broker_url}
                onChange={(e) => setConnectForm({ ...connectForm, token_broker_url: e.target.value })}
                className="sw-input" placeholder="https://…/api/qbo/token?code=…" autoComplete="off" />
              <p className="text-[11px] text-[#94a3b8] mt-1">{t("qbo.broker_url_help")}</p>
            </div>

            <div>
              <label className="sw-label">{t("qbo.environment_label")}</label>
              <select data-testid="qbo-environment" value={connectForm.environment}
                onChange={(e) => setConnectForm({ ...connectForm, environment: e.target.value })} className="sw-input max-w-[220px]">
                <option value="production">{t("qbo.env_production")}</option>
                <option value="sandbox">{t("qbo.env_sandbox")}</option>
              </select>
            </div>

            <button type="button" onClick={() => setAdvancedOpen((v) => !v)}
              className="text-[12px] text-[#475569] underline underline-offset-2" data-testid="qbo-advanced-toggle">
              {advancedOpen ? t("qbo.hide_advanced") : t("qbo.show_advanced")}
            </button>

            {advancedOpen && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-[#eeeded]">
                <p className="md:col-span-2 text-[11px] text-[#94a3b8]">{t("qbo.advanced_hint")}</p>
                <div>
                  <label className="sw-label">{t("qbo.client_id")}</label>
                  <input data-testid="qbo-client-id" value={connectForm.client_id}
                    onChange={(e) => setConnectForm({ ...connectForm, client_id: e.target.value })} className="sw-input" placeholder="AB..." />
                </div>
                <div>
                  <label className="sw-label">{t("qbo.client_secret")}</label>
                  <input data-testid="qbo-client-secret" type="password" value={connectForm.client_secret}
                    onChange={(e) => setConnectForm({ ...connectForm, client_secret: e.target.value })} className="sw-input" placeholder="••••••" />
                </div>
                <div>
                  <label className="sw-label">{t("qbo.realm_id")}</label>
                  <input data-testid="qbo-realm-id" value={connectForm.realm_id}
                    onChange={(e) => setConnectForm({ ...connectForm, realm_id: e.target.value })} className="sw-input" placeholder="9341..." />
                </div>
                <div className="md:col-span-2">
                  <label className="sw-label">{t("qbo.refresh_token")}</label>
                  <input data-testid="qbo-refresh-token" type="password" value={connectForm.refresh_token}
                    onChange={(e) => setConnectForm({ ...connectForm, refresh_token: e.target.value })} className="sw-input" placeholder="RT1-..." />
                  <p className="text-[11px] text-[#94a3b8] mt-1">{t("qbo.refresh_help")}</p>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowConnect(false)} className="sw-btn-secondary">{t("qbo.btn_cancel")}</button>
              <button data-testid="qbo-save-connection" onClick={saveConnection}
                disabled={saving || (!connectForm.token_broker_url && !(connectForm.client_id && connectForm.client_secret && connectForm.realm_id && connectForm.refresh_token))}
                className="sw-btn-primary disabled:opacity-50">
                {saving ? t("qbo.saving") : t("qbo.btn_save")}
              </button>
            </div>
          </div>
        )}
      </Section>

      {/* Section 2: Vendors — source of truth for suppliers */}
      <Section
        id="vendors"
        title={t("qbo.section_vendors_title")}
        subtitle={t("qbo.section_vendors_desc")}
        right={
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-[#94a3b8]"><strong className="text-[#475569]">{vendors.count?.toLocaleString("en-US") || 0}</strong> · {selVendors.length} sel.</span>
            <button onClick={(e) => { e.stopPropagation(); sync("vendors"); }} disabled={syncing.vendors} className="sw-btn-secondary !text-[11px] !py-1" data-testid="qbo-sync-vendors">
              <RefreshCw size={11} className={syncing.vendors ? "animate-spin" : ""} /> {syncing.vendors ? t("qbo.syncing") : t("qbo.sync")}
            </button>
          </div>
        }
      >
        <p className="text-[11px] text-[#94a3b8] mb-3">
          {vendors.last_synced_at ? `${t("qbo.last_sync")} ${vendors.last_synced_at.slice(0, 16).replace("T", " ")} UTC` : t("qbo.never_synced")}
        </p>
        <CatalogList
          testid="qbo-list-vendors"
          items={vendors.items || []}
          columns={[{ key: "Id", label: "Id" }, { key: "DisplayName", label: t("qbo.display_name") }]}
          search={searchV} setSearch={setSearchV}
          selected={selVendors} setSelected={setSelVendors}
          onSaveSelection={() => saveSelection("vendors", selVendors)}
          savingSelection={savingSel.vendors}
        />
      </Section>

      {/* Section 3: Chart of Accounts (import selection only — mapping happens in Travel Policy) */}
      <Section
        id="accounts"
        title={t("qbo.section_coa_title")}
        subtitle={t("qbo.section_coa_desc")}
        right={
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-[#94a3b8]"><strong className="text-[#475569]">{categories.count?.toLocaleString("en-US") || 0}</strong> · {selCategories.length} sel.</span>
            <button onClick={(e) => { e.stopPropagation(); sync("categories"); }} disabled={syncing.categories} className="sw-btn-secondary !text-[11px] !py-1" data-testid="qbo-sync-categories">
              <RefreshCw size={11} className={syncing.categories ? "animate-spin" : ""} /> {syncing.categories ? t("qbo.syncing") : t("qbo.sync")}
            </button>
          </div>
        }
      >
        <p className="text-[11px] text-[#94a3b8] mb-3">
          {categories.last_synced_at ? `${t("qbo.last_sync")} ${categories.last_synced_at.slice(0, 16).replace("T", " ")} UTC` : t("qbo.never_synced")}
        </p>
        <CategoryTree
          testid="qbo-list-categories"
          accounts={categories.items || []}
          selected={selCategories} setSelected={setSelCategories}
          onSaveSelection={() => saveSelection("categories", selCategories)}
          savingSelection={savingSel.categories}
        />
        <div className="mt-3 text-[11px] text-[#94a3b8] italic">
          <AlertTriangle size={11} className="inline mr-1" /> {t("qbo.coa_travel_hint")}
        </div>
      </Section>

      {/* Section 4: Cost codes (QBO Classes) — source of truth */}
      <Section
        id="classes"
        title={t("qbo.section_classes_title")}
        subtitle={t("qbo.section_classes_desc")}
        right={
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-[#94a3b8]"><strong className="text-[#475569]">{classes.count?.toLocaleString("en-US") || 0}</strong> · {selClasses.length} sel.</span>
            <button onClick={(e) => { e.stopPropagation(); sync("classes"); }} disabled={syncing.classes} className="sw-btn-secondary !text-[11px] !py-1" data-testid="qbo-sync-classes">
              <RefreshCw size={11} className={syncing.classes ? "animate-spin" : ""} /> {syncing.classes ? t("qbo.syncing") : t("qbo.sync")}
            </button>
          </div>
        }
      >
        <p className="text-[11px] text-[#94a3b8] mb-3">
          {classes.last_synced_at ? `${t("qbo.last_sync")} ${classes.last_synced_at.slice(0, 16).replace("T", " ")} UTC` : t("qbo.never_synced")}
        </p>
        <CatalogList
          testid="qbo-list-classes"
          items={classes.items || []}
          columns={[{ key: "Id", label: "Id" }, { key: "Name", label: t("common.name") }]}
          search={searchC} setSearch={setSearchC}
          selected={selClasses} setSelected={setSelClasses}
          onSaveSelection={() => saveSelection("classes", selClasses)}
          savingSelection={savingSel.classes}
          extraColumns={[{
            key: "chart_of_accounts",
            label: t("qbo.chart_of_accounts"),
            render: (row) => {
              const classId = String(row.Id);
              const currentAcct = mapping.class_accounts?.[classId] || "";
              return (
                <div className="flex items-center gap-1.5">
                  <select
                    value={currentAcct}
                    onChange={(e) => updateClassAccount(classId, e.target.value)}
                    disabled={savingClassAccount[classId]}
                    className="sw-input !py-1 !text-[11.5px] max-w-[280px]"
                    data-testid={`qbo-class-account-${classId}`}
                  >
                    <option value="">{t("qbo.not_mapped")}</option>
                    {visibleCategoryOptions.map((a) => (
                      <option key={a.Id} value={a.Id}>
                        {"\u00A0".repeat((a._depth || 0) * 2)}{a._depth > 0 ? "↳ " : ""}{a.Name}
                      </option>
                    ))}
                  </select>
                  {savingClassAccount[classId] && <RefreshCw size={11} className="animate-spin text-[#94a3b8]" />}
                  {currentAcct && !savingClassAccount[classId] && <CheckCircle2 size={11} className="text-[#16a34a]" />}
                </div>
              );
            },
          }]}
        />
      </Section>

      {/* Section 5: Departments — list with QBO Class dropdown + full CRUD */}
      <Section
        id="departments"
        title={t("qbo.section_departments_title")}
        subtitle={t("qbo.section_departments_desc")}
        right={
          <button
            onClick={(e) => { e.stopPropagation(); startNewDept(); }}
            className="sw-btn-secondary !text-[11px] !py-1"
            data-testid="qbo-new-dept-btn"
          >
            <Plus size={11} /> {t("qbo.new_dept")}
          </button>
        }
      >
        {(classes.items || []).length === 0 && (
          <div className="flex items-center gap-2 text-[12px] text-[#a38d5b] bg-[#fcf3e2] border border-[#dac28a] rounded-[10px] p-3 mb-4">
            <AlertTriangle size={14} /> {t("qbo.sync_classes_first")}
          </div>
        )}

        {deptEditing && (
          <form onSubmit={saveDept} className="mb-4 p-4 border-2 border-[#e2e8f0] rounded-[12px] bg-[#f8fafc]" data-testid="qbo-dept-form">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] uppercase tracking-[0.12em] text-[#475569] font-semibold">
                {deptEditing === "new" ? t("qbo.new_dept") : t("qbo.edit_dept")}
              </div>
              <button type="button" onClick={cancelDept} className="text-[#94a3b8]"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="sw-label">{t("common.name")} *</label>
                <input required value={deptForm.name} onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })} className="sw-input !text-[12.5px]" data-testid="qbo-dept-form-name" />
              </div>
              <div>
                <label className="sw-label">Cost code *</label>
                <input required value={deptForm.cost_code} onChange={(e) => setDeptForm({ ...deptForm, cost_code: e.target.value })} className="sw-input !text-[12.5px]" data-testid="qbo-dept-form-code" />
              </div>
              <div>
                <label className="sw-label">{t("qbo.parent_dept")}</label>
                <select value={deptForm.parent_id} onChange={(e) => setDeptForm({ ...deptForm, parent_id: e.target.value })} className="sw-input !text-[12.5px]">
                  <option value="">—</option>
                  {departments.filter((d) => d.id !== deptEditing?.id).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button type="button" onClick={cancelDept} className="sw-btn-secondary !text-[12px]">{t("common.cancel")}</button>
              <button type="submit" disabled={savingDeptForm} className="sw-btn-primary !text-[12px]" data-testid="qbo-dept-form-save">
                <Save size={12} /> {savingDeptForm ? "..." : t("common.save")}
              </button>
            </div>
          </form>
        )}

        <div className="sw-card !p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-[#f8fafc]">
                <tr>
                  <th className="text-left px-4 py-2 text-[10.5px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">{t("common.name")}</th>
                  <th className="text-left px-4 py-2 text-[10.5px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">Cost code</th>
                  <th className="text-left px-4 py-2 text-[10.5px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">{t("qbo.qbo_class")}</th>
                  <th className="text-right px-4 py-2 text-[10.5px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold w-[120px]">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {departments.map((d) => {
                  const availableClasses = (classes.items || []).filter((c) => selClasses.length === 0 || selClasses.includes(String(c.Id)));
                  return (
                    <tr key={d.id} className="border-t border-[#f1f5f9] hover:bg-[#f8fafc]" data-testid={`qbo-dept-row-${d.id}`}>
                      <td className="px-4 py-2 font-medium text-[#0f172a]">
                        {d.name}
                        {d.parent_id && (
                          <div className="text-[10.5px] text-[#94a3b8]">↳ {departments.find((x) => x.id === d.parent_id)?.name}</div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-[#475569]"><code className="text-[11.5px]">{d.cost_code}</code></td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <select
                            value={d.qbo_class_id || ""}
                            onChange={(e) => updateDeptClass(d.id, e.target.value)}
                            disabled={savingDept[d.id]}
                            className="sw-input !py-1.5 !text-[12px] max-w-[280px]"
                            data-testid={`qbo-dept-class-${d.id}`}
                          >
                            <option value="">{t("qbo.not_mapped")}</option>
                            {availableClasses.map((c) => (
                              <option key={c.Id} value={c.Id}>{c.Name}</option>
                            ))}
                          </select>
                          {savingDept[d.id] && <RefreshCw size={12} className="animate-spin text-[#94a3b8]" />}
                          {d.qbo_class_id && !savingDept[d.id] && <CheckCircle2 size={13} className="text-[#16a34a]" />}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => startEditDept(d)}
                          className="text-[#475569] hover:bg-[#e2e8f0] px-2 py-1 rounded-[6px] text-[11.5px] font-medium inline-flex items-center gap-1"
                          data-testid={`qbo-dept-edit-${d.id}`}
                        >
                          <Pencil size={11} /> {t("common.edit")}
                        </button>
                        <button
                          type="button"
                          onClick={() => removeDept(d)}
                          className="text-[#881a27] hover:bg-[#e5c1c1] px-2 py-1 rounded-[6px] text-[11.5px] font-medium inline-flex items-center gap-1 ml-1"
                          data-testid={`qbo-dept-delete-${d.id}`}
                        >
                          <Trash2 size={11} /> {t("common.delete")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {departments.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-[12px] text-[#94a3b8]">{t("qbo.no_departments")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Section>
    </div>
  );
}
